import { APP_CONFIG } from '../shared/config';
import { CartItem, DedicationType } from './CartContext';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Stored on the cart item only; the name shown to donors is always resolved live. */
export const UNITED_WAY_PLACEHOLDER_NAME = 'United Way';

/** Empty when unconfigured or malformed, which disables the whole feature. */
export function getUnitedWayAgencyId(): string {
  const id = APP_CONFIG.UnitedWay.AgencyId?.trim().toLowerCase() ?? '';
  return GUID_PATTERN.test(id) ? id : '';
}

export function getUnitedWaySupportAmount(): number {
  return APP_CONFIG.UnitedWay.SupportAmountInCents;
}

export function matchesUnitedWayAgency(item: CartItem, agencyId: string): boolean {
  return !!agencyId && item.agencyId?.toLowerCase() === agencyId;
}

/**
 * Classified by the marker alone. Involving the configured id would let a settings change
 * reclassify a persisted add-on as an ordinary donation to a stale agency.
 */
export function isUnitedWaySupportItem(item: CartItem): boolean {
  return item.isUnitedWaySupport === true;
}

export function hasUnitedWaySupport(items: ReadonlyArray<CartItem>): boolean {
  return items.some(isUnitedWaySupportItem);
}

export function findUnitedWaySupportItem(items: ReadonlyArray<CartItem>): CartItem | undefined {
  return items.find(isUnitedWaySupportItem);
}

/** A persisted add-on that no longer points at the configured agency, so it can't be submitted. */
export function hasStaleUnitedWaySupport(
  items: ReadonlyArray<CartItem>,
  agencyId: string
): boolean {
  return items.some(
    item => isUnitedWaySupportItem(item) && !matchesUnitedWayAgency(item, agencyId)
  );
}

/** A United Way donation added from its agency page, which behaves like any other donation. */
export function hasStandardUnitedWayDonation(
  items: ReadonlyArray<CartItem>,
  agencyId: string
): boolean {
  return items.some(
    item => matchesUnitedWayAgency(item, agencyId) && !isUnitedWaySupportItem(item)
  );
}

export function createUnitedWaySupportItem(agencyId: string, amount: number): CartItem {
  return {
    agencyId,
    agencyName: UNITED_WAY_PLACEHOLDER_NAME,
    amount,
    dedication: { dedicationType: DedicationType.None },
    isUnitedWaySupport: true,
  };
}

export function withoutUnitedWaySupport(items: ReadonlyArray<CartItem>): Array<CartItem> {
  return items.filter(item => !isUnitedWaySupportItem(item));
}
